"use client"

import type { FieldConfiguration, CustomValue } from "@/types/workflow"
import { getLocalizedField } from '@/utils/language-utils'
import { useTranslation } from "@/hooks/use-translation"
import { useLanguage } from "@/context/language-context"
import { CustomDropdownField } from "@/components/custom-dropdown-field"

interface DropdownCellProps {
  value: string | string[] | null
  options: Array<{ value: string; label?: string; color?: string }>
  customValues?: CustomValue[]
  rowId: string
  column: FieldConfiguration
  onSave: (value: string | string[] | null) => void
  useColorCode?: boolean
  multiSelect?: boolean
  displayText?: string
  useActionStyling?: boolean
}

interface DropdownCellEditorProps {
  value: string | null
  customValues: CustomValue[]
  onChange: (value: string) => void
}

export function DropdownCell({
  value,
  options,
  customValues,
  rowId,
  column,
  onSave,
  useColorCode = false,
  multiSelect = false,
  displayText,
  useActionStyling = false,
}: DropdownCellProps) {
  const { t, language } = useTranslation()

  // Helper function to get display value from stored code
  const getDisplayValue = (storedValue: string): string => {
    if (customValues) {
      const customValue = customValues.find(cv => cv.code === storedValue)
      return customValue ? getLocalizedField(customValue, 'title', language) : storedValue
    }
    return storedValue
  }

  // Helper function to get stored code from display value
  const getStoredCode = (displayValue: string): string => {
    if (customValues) {
      const customValue = customValues.find(cv => getLocalizedField(cv, 'title', language) === displayValue)
      return customValue ? customValue.code : displayValue
    }
    return displayValue
  }

  // Convert single value to array for multi-select
  const selectedValues = multiSelect 
    ? (Array.isArray(value) ? value : value ? [value] : []) 
    : (typeof value === 'string' && value !== '' ? [value] : [])

  // Format options for the dropdown - use customValues if available, otherwise fall back to options
  const dropdownOptions = customValues 
    ? customValues.map((customValue) => ({
        value: customValue.code,
        label: getLocalizedField(customValue, 'title', language),
        color: customValue.color
      }))
    : options.map((option) => ({
        value: option.value,
        label: option.label || option.value,
        color: option.color
      }))

  const handleValueChange = (newValues: string[]) => {
    if (multiSelect) {
      onSave(newValues)
    } else if (newValues.length > 0) {
      onSave(newValues[0])
    } else {
      // Send null when clearing the value
      onSave(null)
    }
  }

  // For action columns, show custom styling
  if (useActionStyling) {
    return (
      <CustomDropdownField
        options={dropdownOptions}
        selectedValues={selectedValues}
        onChange={handleValueChange}
        placeholder={displayText || t('selectOption')}
        useColorCode={useColorCode}
        multiSelect={false}
        className="bg-[#26313E] text-white hover:bg-[#1e2832] z-[100]"
        showSearch={false}
        allowClear={!!customValues && customValues.length > 0}
      />
    )
  }

  // Default dropdown
  return (
    <CustomDropdownField
      options={dropdownOptions}
      selectedValues={selectedValues}
      onChange={handleValueChange}
      placeholder={displayText || t('selectOption')}
      useColorCode={useColorCode}
      multiSelect={multiSelect}
      className="z-[100]"
      showSearch={false}
      allowClear={!multiSelect && !!customValues && customValues.length > 0}
    />
  )
}

export function DropdownCellEditor({ value, customValues, onChange }: DropdownCellEditorProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  const options = customValues.map(cv => ({
    value: cv.code,
    label: getLocalizedField(cv, 'title', language),
    color: cv.color
  }));
  
  return (
    <CustomDropdownField
      options={options}
      selectedValues={value ? [value] : []}
      onChange={values => onChange(values[0] || '')}
      placeholder={t('selectOption')}
      useColorCode={true}
      multiSelect={false}
      showSearch={false}
      allowClear={customValues.length > 0}
    />
  );
}
